import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/store';
import type { LineageNode, LineageTree } from '../../lib/types';
import { Button, ErrorState, Panel } from '../ui';

/**
 * The Family Tree — design PART 5.
 *
 * Rendered as nested columns growing to the *right*, one column per generation
 * back, rather than as the pyramid a family tree usually is. A pyramid doubles
 * in width every generation and is unreadable by the third; columns grow in a
 * direction the page already scrolls, and a deep line reads as a long thread
 * back through time, which is closer to what it actually is.
 *
 * Nothing here fetches more than the server offers in one slice. A node that
 * still has ancestors above the returned depth gets a button that re-roots the
 * whole panel at that ancestor — which is the lazy loading the design asks for,
 * done without a second endpoint or a cache to keep coherent.
 */

interface Props {
  pokemonId: string;
  /** Shown as the crumb trail's first entry so re-rooting can be undone. */
  rootLabel: string;
}

export function FamilyTree({ pokemonId, rootLabel }: Props) {
  // The crumb trail of ancestors the player has climbed into. The last entry is
  // what is currently displayed; the earlier ones are how they get back.
  const [trail, setTrail] = useState<Array<{ id: string; label: string }>>([
    { id: pokemonId, label: rootLabel },
  ]);
  const current = trail[trail.length - 1]!;

  const state = useAsync<LineageTree>(
    () => api.get<LineageTree>(`/pokemon/${current.id}/lineage?depth=3`),
    [current.id],
  );

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <p className="tiny muted">Tracing the family…</p>;

  const { root, truncated } = state.data;

  const climb = (node: LineageNode) =>
    setTrail((previous) => [...previous, { id: node.id, label: nameOf(node) }]);

  return (
    <div className="stack-sm">
      {trail.length > 1 && (
        <div className="row row--wrap tiny">
          {trail.map((step, index) => (
            <span key={step.id}>
              {index > 0 && <span className="muted"> ← </span>}
              {index === trail.length - 1 ? (
                <strong>{step.label}</strong>
              ) : (
                <button
                  type="button"
                  className="lineage__crumb"
                  onClick={() => setTrail((previous) => previous.slice(0, index + 1))}
                >
                  {step.label}
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {!root.hasMoreAncestors ? (
        <p className="tiny muted">
          No parents are recorded for this one. {root.statusLabel === 'Bred' ? '' : 'Its line starts here.'}
        </p>
      ) : (
        <div className="lineage">
          <TreeColumn nodes={[root]} onClimb={climb} />
        </div>
      )}

      {truncated && (
        <p className="tiny muted">
          The line goes back further than this view. Open an ancestor to keep climbing.
        </p>
      )}
    </div>
  );
}

/** One generation. Recurses into its parents' column to the right. */
function TreeColumn({ nodes, onClimb }: { nodes: LineageNode[]; onClimb: (node: LineageNode) => void }) {
  return (
    <ul className="lineage__col">
      {nodes.map((node) => (
        <li key={node.id} className="lineage__branch">
          <NodeCard node={node} onClimb={onClimb} />
          {node.parents.length > 0 && <TreeColumn nodes={node.parents} onClimb={onClimb} />}
        </li>
      ))}
    </ul>
  );
}

function nameOf(node: LineageNode) {
  return node.nickname ?? node.speciesName;
}

/**
 * One Pokemon in the tree.
 *
 * A node whose Pokemon still exists is a link. One whose Pokemon is gone is
 * deliberately *not* dimmed into illegibility — it is marked, and it is marked
 * honestly ("no longer around"), because an ancestor that was released is still
 * part of the story and the whole reason its record was kept.
 */
function NodeCard({ node, onClimb }: { node: LineageNode; onClimb: (node: LineageNode) => void }) {
  const body = (
    <>
      {node.sprite && <img className="pixel lineage__sprite" src={node.sprite} alt="" loading="lazy" />}
      <span className="lineage__text">
        <span className="lineage__name">
          {node.isShiny && <span className="lineage__shiny" title="Shiny">✦</span>}
          {nameOf(node)}
        </span>
        <span className="tiny muted">
          Gen {node.generation}
          {node.ownerName ? ` · ${node.ownerName}` : ' · no longer around'}
        </span>
      </span>
    </>
  );

  return (
    <div className={`lineage__node ${node.exists ? '' : 'lineage__node--ghost'}`}>
      {node.exists ? (
        <Link className="lineage__link" to={`/pokemon/${node.id}`}>
          {body}
        </Link>
      ) : (
        <div className="lineage__link">{body}</div>
      )}
      {node.hasMoreAncestors && node.parents.length === 0 && (
        <Button size="sm" onClick={() => onClimb(node)}>
          ↑ Earlier
        </Button>
      )}
    </div>
  );
}

/** The panel wrapper used on a Pokemon's page, so the page stays declarative. */
export function LineagePanel({ pokemonId, rootLabel }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <Panel title="⑃ Lineage" hint={open ? undefined : 'Family tree'}>
      {open ? (
        <FamilyTree pokemonId={pokemonId} rootLabel={rootLabel} />
      ) : (
        <Button size="sm" onClick={() => setOpen(true)}>
          View Lineage
        </Button>
      )}
    </Panel>
  );
}
